import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Star, Minus, Plus, ShoppingCart, ArrowLeft, Check, BadgeCheck, Shield, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { OfficialMerchBadge } from '../components/OfficialMerchBadge';
import DropHintModal from '../components/DropHintModal';
import ProcurementProcedure from '../components/ProcurementProcedure';
import { useAuth, useCart, API } from '../App';

const VerificationBadge = ({ verification, size = 'md' }) => {
  const sizeClasses = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  const textClasses = size === 'lg' ? 'text-sm' : 'text-xs';
  
  if (verification === 'trusted') {
    return (
      <div className={`inline-flex items-center gap-1.5 ${textClasses} text-amber-600 bg-amber-50 px-2 py-1 rounded-full`} title="Trusted Vendor">
        <Shield className={`${sizeClasses} fill-amber-500 text-amber-600`} />
        <span className="font-medium">Trusted Seller</span>
      </div>
    );
  }
  if (verification === 'verified') {
    return (
      <div className={`inline-flex items-center gap-1.5 ${textClasses} text-secondary bg-secondary/10 px-2 py-1 rounded-full`} title="Verified Vendor">
        <BadgeCheck className={sizeClasses} />
        <span className="font-medium">Verified Seller</span>
      </div>
    );
  }
  return null;
};

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [hintOpen, setHintOpen] = useState(false);
  
  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/reviews/${productId}`)
        ]);
        setProduct(productRes.data);
        setReviews(reviewsRes.data.reviews || []);
        // Auto-select the first variant for POD products with multiple variants
        const variants = productRes.data?.printful_variants || [];
        if (variants.length > 1) {
          setSelectedVariantId(variants[0]?.sync_variant_id || null);
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    const variants = product?.printful_variants || [];
    const hasMultipleVariants = variants.length > 1;
    if (hasMultipleVariants && !selectedVariantId) {
      toast.error('Please select a size / variant first');
      return;
    }

    setAdding(true);
    try {
      const options = {};
      if (hasMultipleVariants && selectedVariantId) {
        options.variant_id = selectedVariantId;
        const v = variants.find((x) => x.sync_variant_id === selectedVariantId);
        if (v?.name) options.variant_label = v.name;
      }
      await addToCart(product.product_id, quantity, options);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to leave a review');
      return;
    }
    
    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/reviews/${productId}`, {
        rating: reviewRating,
        comment: reviewComment
      });
      setReviews([response.data, ...reviews]);
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      toast.success('Review submitted!');
      
      // Refresh product to get updated rating
      const productRes = await axios.get(`${API}/products/${productId}`);
      setProduct(productRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
          <div className="animate-pulse grid md:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted rounded-lg" />
            <div className="space-y-4">
              <div className="h-8 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-24 bg-muted rounded" />
              <div className="h-12 bg-muted rounded w-32" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-medium mb-4">Product Not Found</h1>
            <Link to="/products">
              <Button>Back to Products</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
        {/* Breadcrumb */}
        <Link to="/products" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8" data-testid="back-to-products">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-muted mb-4">
              <img 
                src={product.images[selectedImage] || product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={img || `img-${i}`}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      selectedImage === i ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <Badge variant="secondary" className="mb-4">{product.category}</Badge>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-foreground mb-2" data-testid="product-title">
              {product.name}
            </h1>
            <div className="flex items-center gap-3 mb-4">
              <p className="text-muted-foreground">by {product.vendor_name}</p>
              {product.vendor_id === 'admin' && product.fulfilled_by_printful ? (
                <OfficialMerchBadge size="lg" />
              ) : (
                <VerificationBadge verification={product.vendor_verification} size="lg" />
              )}
            </div>
            
            {/* Rating — only show when the product has been reviewed */}
            {typeof product.rating === 'number' && product.rating > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center">
                  {['star1','star2','star3','star4','star5'].map((k, i) => (
                    <Star
                      key={k}
                      className={`h-5 w-5 ${i < Math.round(product.rating) ? 'fill-primary text-primary' : 'text-muted'}`}
                    />
                  ))}
                </div>
                <span className="text-muted-foreground">
                  {product.rating.toFixed(1)} ({product.review_count || 0} reviews)
                </span>
              </div>
            )}

            <p className="text-lg text-foreground/80 leading-relaxed mb-8 whitespace-pre-line">
              {product.description}
            </p>

            {/* Spec sheet — for commodity products with structured specifications */}
            {product.spec_sheet && Object.keys(product.spec_sheet).length > 0 && (
              <div className="mb-8 bg-muted/30 rounded-xl border border-border/40 p-5" data-testid="spec-sheet">
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-primary mb-3">Specifications</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {Object.entries(product.spec_sheet).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 py-1 border-b border-border/30">
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="text-foreground font-medium text-right">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {product.pricing_type === 'market' ? (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-6" data-testid="market-pricing-block">
                <p className="text-2xl font-heading font-semibold text-primary leading-tight">
                  Prevailing Market Price
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Final price quoted at order placement based on prevailing market rates.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 bg-secondary/10 text-secondary px-3 py-1.5 rounded-full text-sm font-medium">
                  Marketplace commission: {product.commission_text || `${product.commission_pct ?? 0}%`}
                </div>
              </div>
            ) : (
              <div className="text-3xl font-heading font-semibold text-primary mb-6" data-testid="product-price">
                ${product.price.toFixed(2)}
              </div>
            )}

            {/* Variant selector — only for POD products with multiple variants */}
            {product.printful_variants && product.printful_variants.length > 1 && (
              <div className="mb-6" data-testid="variant-selector">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Choose your option
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.printful_variants.map((v) => {
                    const isSelected = selectedVariantId === v.sync_variant_id;
                    return (
                      <button
                        key={v.sync_variant_id}
                        type="button"
                        onClick={() => setSelectedVariantId(v.sync_variant_id)}
                        className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                            : 'border-border bg-background text-foreground hover:border-primary/60'
                        }`}
                        data-testid={`variant-${v.sync_variant_id}`}
                      >
                        {v.name || v.sku || `Variant ${v.sync_variant_id}`}
                        {v.retail_price && (
                          <span className="ml-2 opacity-80">${parseFloat(v.retail_price).toFixed(2)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stock — hidden for market-priced products */}
            {product.pricing_type !== 'market' && (
              <div className="flex items-center gap-2 mb-6">
                {product.quantity > 0 ? (
                  <>
                    <Check className="h-5 w-5 text-secondary" />
                    <span className="text-secondary font-medium">In Stock ({product.quantity} available)</span>
                  </>
                ) : (
                  <span className="text-destructive font-medium">Out of Stock</span>
                )}
              </div>
            )}

            {/* Quantity & Add to Cart — or Request Quote / or Vendors Needed banner */}
            {product.vendor_needed ? (
              <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-5 space-y-3" data-testid="vendor-needed-banner">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center bg-primary text-primary-foreground text-xs font-bold tracking-wider px-3 py-1 rounded-full">
                    VENDORS NEEDED
                  </span>
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground">
                  Be the first vendor in <span className="text-primary">{product.category}</span>
                </h3>
                <p className="text-sm text-muted-foreground">
                  This category is open for new vendors. Join Christ Kingdom Marketplace as a Founding Vendor and start selling worldwide.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li>✅ Keep 90% of every sale — we take only 10%</li>
                  <li>✅ FREE to list — zero monthly fees</li>
                  <li>✅ Global Stripe payouts to 195 countries</li>
                  <li>✅ Free "Trusted Vendor" badge from day 1</li>
                </ul>
                <Button
                  size="lg"
                  className="w-full rounded-xl"
                  onClick={() => navigate(`/register?role=vendor&category=${encodeURIComponent(product.category || '')}`)}
                  data-testid="become-vendor-button"
                >
                  Become a Vendor — It's Free
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Already a vendor? <Link to="/login" className="text-primary underline">Sign in</Link> and list your product.
                </p>
              </div>
            ) : product.pricing_type === 'market' ? (
              <div className="space-y-3">
                <Button
                  size="lg"
                  className="w-full rounded-xl"
                  onClick={async () => {
                    // Fire-and-forget: create quote in CRM + email to admin
                    try {
                      await axios.post(`${API}/products/${product.product_id}/request-quote`, {
                        product_id: product.product_id,
                        buyer_name: user?.name,
                        buyer_email: user?.email,
                      });
                      toast.success('Quote request received — we\'ll reply within 1 business day.');
                    } catch (err) {
                      // Non-blocking: still open mailto fallback
                      console.warn('Quote request CRM sync failed, falling back to mailto:', err?.message || err);
                    }
                    const subject = encodeURIComponent(`Quote Request: ${product.name}`);
                    const body = encodeURIComponent(
                      `Hi Christ Kingdom Marketplace team,\n\nI'd like to request a quote for:\n\nProduct: ${product.name}\nVendor: ${product.vendor_name}\nMy quantity / volume: \nDelivery destination: \nMy contact phone: \n\nThank you.`
                    );
                    window.location.href = `mailto:support@christkingdomglobalmarketplace.com?subject=${subject}&body=${body}`;
                  }}
                  data-testid="request-quote-button"
                >
                  Request Quote
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Our team replies with a custom quote within 1 business day. No obligation.
                </p>
              </div>
            ) : (
              product.quantity > 0 && (
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center border border-border rounded-lg">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-12 text-center font-medium" data-testid="quantity-display">{quantity}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setQuantity(q => Math.min(product.quantity, q + 1))}
                      disabled={quantity >= product.quantity}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    size="lg"
                    className="flex-1 rounded-xl"
                    onClick={handleAddToCart}
                    disabled={adding}
                    data-testid="add-to-cart-button"
                  >
                    {adding ? (
                      'Adding...'
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              )
            )}

            {/* Drop a Hint — gift hint email to a friend */}
            {!product.vendor_needed && product.pricing_type !== 'market' && (
              <button
                type="button"
                onClick={() => setHintOpen(true)}
                className="mt-3 inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
                data-testid="drop-hint-trigger"
              >
                <Gift className="h-4 w-4" />
                Drop a hint to someone
              </button>
            )}

            {/* Social Share */}
            <ShareButtons
              url={typeof window !== 'undefined' ? window.location.href : ''}
              title={`${product.name} — Christ Kingdom Global Marketplace`}
              description={(product.description || '').slice(0, 140)}
            />
          </div>
        </div>

        {/* Reviews Section */}
        <section className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-heading font-medium">Customer Reviews</h2>
            {user && !showReviewForm && (
              <Button variant="outline" onClick={() => setShowReviewForm(true)} data-testid="write-review-button">
                Write a Review
              </Button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <form onSubmit={handleSubmitReview}>
                  <h3 className="font-heading font-medium mb-4">Write Your Review</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewRating(star)}
                          className="p-1"
                        >
                          <Star 
                            className={`h-6 w-6 ${star <= reviewRating ? 'fill-primary text-primary' : 'text-muted'}`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Your Review</label>
                    <Textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Share your thoughts about this product..."
                      rows={4}
                      required
                      data-testid="review-textarea"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={submittingReview} data-testid="submit-review-button">
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowReviewForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No reviews yet. Be the first to review this product!</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.review_id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{review.user_name}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {['rs1','rs2','rs3','rs4','rs5'].map((k, i) => (
                            <Star 
                              key={`${review.review_id || review.user_name}-${k}`} 
                              className={`h-4 w-4 ${i < review.rating ? 'fill-primary text-primary' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-foreground/80 mt-3">{review.comment}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Procurement procedure — only for market-priced commodity products */}
        {product.pricing_type === 'market' && <ProcurementProcedure />}
      </main>

      <DropHintModal product={product} open={hintOpen} onClose={() => setHintOpen(false)} />

      <Footer />
    </div>
  );
}
